import React, { useState } from 'react';
import { Box, Paper, IconButton, Tooltip } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import pako from 'pako';
import { GRID_LOCALE_FR } from "../../../config/translations/data-table/localFr";
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import JSZip from 'jszip';
import { PiMicrosoftExcelLogoFill } from "react-icons/pi";
import {
    GridColumnMenuContainer,
    GridColumnMenuFilterItem,
    GridColumnMenuManageItem,
    useGridApiContext
} from '@mui/x-data-grid';
import { MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';

const TARGET_FILTER_FIELD = 'dateEdition';
const LAST_COLUMN_FIELD = 'action';
const DATE_FIELD = 'dateDecompte';
/** --- Menu de colonne personnalisé, visible UNIQUEMENT pour la dernière colonne --- */
function CustomColumnMenu(props) {
    const { hideMenu, open } = props;
    const col = props.currentColumn ?? props.colDef;
    const apiRef = useGridApiContext();

    if (!col || col.field !== LAST_COLUMN_FIELD) return null;

    const openDateFilter = () => {
        // Make the filter panel open and preselect "Date d'édition" with a valid date operator
        const fm = apiRef.current.getFilterModel?.() ?? { items: [] };
        const items = fm.items?.length
            ? [...fm.items]
            : [{ id: 1, field: DATE_FIELD, operator: 'is', value: null }];

        items[0] = { ...items[0], field: DATE_FIELD, operator: 'is', value: null };

        apiRef.current.setFilterModel({ ...fm, items });
        apiRef.current.showFilterPanel();
        hideMenu?.();
    };

    return (
        <GridColumnMenuContainer hideMenu={hideMenu} currentColumn={col} open={open}>
            {/* Use a custom item instead of GridColumnMenuFilterItem */}
            <MenuItem onClick={openDateFilter}>
                <ListItemIcon><FilterListIcon fontSize="small" /></ListItemIcon>
                <ListItemText>Filtrer</ListItemText>
            </MenuItem>

            <GridColumnMenuManageItem onClick={hideMenu} column={col} colDef={col} />
        </GridColumnMenuContainer>
    );
}

const DecomptesTable = ({ rows, onViewDetails }) => {
    const [selectionModel, setSelectionModel] = useState([]);
    const [columnVisibilityModel, setColumnVisibilityModel] = React.useState({
        id: false,
        firstName: true,
        lastName: true,
        age: true,
        fullName: true,
        // on force la colonne action à true (toujours visible)
        [LAST_COLUMN_FIELD]: true,
    });

    const handleDownloadPdf = (gzippedBase64, fileName) => {
        if (!gzippedBase64) {
            console.error('No PDF data to download');
            return;
        }
        const now = new Date();
        const timestamp = now.toISOString().replace(/[-:]/g, '').replace(/\..+$/, '');
        const baseName = fileName ? fileName.replace(/\.pdf$/i, '') : 'decompte';
        const usedFilename = `${baseName}_${timestamp}.pdf`;

        const binaryString = atob(gzippedBase64);
        const len = binaryString.length;
        const compressedBytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) compressedBytes[i] = binaryString.charCodeAt(i);

        let pdfBytes;
        try {
            pdfBytes = pako.ungzip(compressedBytes);
        } catch (err) {
            console.error('Failed to ungzip PDF bytes:', err);
            return;
        }

        const blob = new Blob([pdfBytes], { type: 'application/pdf' });
        const blobUrl = URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = usedFilename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
    };

    const handleExportSelectedRows = () => {
        if (selectionModel.length === 0) return;

        const selectedRows = rows.filter(row => selectionModel.includes(row.id));
        const csvHeaders = ['Date d\'édition', 'Date début de période', 'Date fin de période', 'Montant total'];
        const csvKeys = ['dateDecompte', 'dateDebutPeriode', 'dateFinPeriode', 'montantTotal'];

        const csvContent = [
            csvHeaders.join(';'),
            ...selectedRows.map(row =>
                csvKeys.map(key => {
                    const val = row[key];
                    if (typeof val === 'number') {
                        return val.toString().replace('.', ',');
                    }
                    return `"${(val ?? '').toString().replace(/"/g, '""')}"`;
                }).join(';')
            )
        ].join('\n');

        // Ajout du BOM UTF-8
        const BOM = '\uFEFF';
        const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });

        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        const now = new Date();
        const timestamp = now.toISOString().replace(/[-:]/g, '').replace(/\..+$/, '');

        link.href = url;
        link.setAttribute('download', 'list_decomptes_' + timestamp + '.csv');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const handleDownloadSelectedPDFs = async () => {
        if (selectionModel.length === 0) {
            console.warn('Aucune ligne sélectionnée.');
            return;
        }

        const zip = new JSZip();
        const selectedRows = rows.filter(row => selectionModel.includes(row.id));

        const now = new Date();
        const timestamp = now.toISOString().replace(/[-:]/g, '').replace(/\..+$/, '');

        for (const row of selectedRows) {
            const gzippedBase64 = row.base64;
            const fileNameBase = row.filename ? row.filename.replace(/\.pdf$/i, '') : `decompte_${row.id}`;
            const usedFilename = `${fileNameBase}_${timestamp}.pdf`;

            try {
                const binaryString = atob(gzippedBase64);
                const len = binaryString.length;
                const compressedBytes = new Uint8Array(len);
                for (let i = 0; i < len; i++) compressedBytes[i] = binaryString.charCodeAt(i);

                const pdfBytes = pako.ungzip(compressedBytes);
                zip.file(usedFilename, pdfBytes);
            } catch (err) {
                console.error(`Erreur lors du traitement du PDF pour ${fileNameBase}:`, err);
            }
        }

        try {
            const zipBlob = await zip.generateAsync({ type: 'blob', mimeType: 'application/zip' });
            const zipFilename = `decomptes_${timestamp}.zip`;

            if (window.navigator && window.navigator.msSaveOrOpenBlob) {
                window.navigator.msSaveOrOpenBlob(zipBlob, zipFilename);
            } else {
                const zipUrl = URL.createObjectURL(zipBlob);
                const link = document.createElement('a');
                link.href = zipUrl;
                link.download = zipFilename;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(zipUrl);
            }
        } catch (error) {
            console.error('Erreur lors de la génération du fichier ZIP:', error);
        }
    };

    const parseFrDate = (s) => {
        if (!s) return null;
        const [d, m, y] = s.split('/').map(Number);
        return new Date(y, m - 1, d);
    };
// helper (outside columns)
    const parseEuro = (val) => {
        if (val == null) return null;
        if (typeof val === 'number') return val;

        let s = String(val)
            .replace(/€/g, '')            // remove €
            .replace(/[\s\u00A0\u202F]/g, ''); // remove spaces (normal, nbsp, narrow nbsp)

        // ✅ replace comma with dot (decimal separator)
        s = s.replace(',', '.');

        const n = Number(s);
        return Number.isNaN(n) ? null : n;
    };
    const columns = [
       // { field: 'id', headerName: 'ID', width: 100, disableColumnMenu: true },
        {
            field: 'dateDecompte',
            headerName: "Date d'édition",
            width: 200,
            type: 'date',
            valueGetter: (value) => parseFrDate(value),
            valueFormatter: (value) => value ? new Intl.DateTimeFormat('fr-FR').format(value) : 'fails',
            disableColumnMenu: true,
        },
        {
            field: 'dateDebutPeriode',
            headerName: 'Date début de période',
            width: 250,
            type: 'date',
            valueGetter: (value) => parseFrDate(value),
            valueFormatter: (value) => value ? new Intl.DateTimeFormat('fr-FR').format(value) : 'fails',
            disableColumnMenu: true,
        },
        {
            field: 'dateFinPeriode',
            headerName: 'Date fin de période',
            width: 250,
            type: 'date',
            valueGetter: (value) => parseFrDate(value),
            valueFormatter: (value) => value ? new Intl.DateTimeFormat('fr-FR').format(value) : 'fails',
            disableColumnMenu: true,
        },
        {
            field: 'montantTotal',
            headerName: 'Montant total',
            flex: 1,
            headerAlign: 'right',   // ✅ alignement du header à droite
            align: 'right',         // ✅ alignement des cellules à droite
            type: 'text',
            valueGetter: (value) => parseEuro(value),
            valueFormatter: (value) => {
                const amount = typeof value === 'string' ? parseFloat(value) : value;
                return amount.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
            },
            disableColumnMenu: true,
        },
        {
            field: LAST_COLUMN_FIELD,
            headerName: 'Actions',
            width: 200,
            sortable: false,
            headerAlign: 'center',
            align: 'center',
            filterable: false,
            disableColumnFilter: true,
            // 👉 garder le menu de colonne ACTIF uniquement ici
            disableColumnMenu: false,

            // 👉 empêcher sa dissimulation via la Toolbar/visibilité
            hideable: false, // si tu es en DataGridPro v6+
            headerClassName: 'always-menu',
            renderCell: (params) => (
                <>
                    <IconButton
                        variant="contained"
                        color="error"
                        size="small"
                        onClick={(e) => { e.stopPropagation(); handleDownloadPdf(params.row.base64, params.row.filename); }}
                    >
                        <Tooltip title="Télécharger le décompte"><PictureAsPdfIcon /></Tooltip>
                    </IconButton>
                    <IconButton
                        variant="contained"
                        color="secondary"
                        size="small"
                        onClick={(e) => { e.stopPropagation(); onViewDetails(params.row.id); }}
                    >
                        <Tooltip title="Visualiser les détails du décompte"><ManageSearchIcon /></Tooltip>
                    </IconButton>
                </>
            )
        }
    ];

    return (
        <Box sx={{ p: 1.5, borderRadius: '8px', width: '100%' }}>
            <Paper sx={{ padding: 5 }}>
                {selectionModel.length > 0 && (
                    <Box mb={2} display="flex" justifyContent="flex-end">
                        <Tooltip title="Exporter le tableau des décomptes">
                            <IconButton variant="contained" color="success" onClick={handleExportSelectedRows}>
                                <PiMicrosoftExcelLogoFill />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Télécharger les décomptes">
                            <IconButton variant="contained" color="error" onClick={handleDownloadSelectedPDFs}>
                                <FileDownloadIcon />
                            </IconButton>
                        </Tooltip>
                    </Box>
                )}

                <DataGrid
                    rows={rows}
                    columns={columns}
                    pageSizeOptions={[10, 20, 50]}
                    // 👉 brancher notre menu personnalisé (il ne s’affichera que sur la dernière colonne)
                    slots={{
                        columnMenu: CustomColumnMenu,
                    }}
                    // Texte FR
                    localeText={GRID_LOCALE_FR}
                    // Libellé pagination
                    componentsProps={{
                        pagination: {
                            labelRowsPerPage: 'Nombre de lignes par page',
                        }
                    }}
                    initialState={{
                        pagination: {
                            paginationModel: { pageSize: 10 },
                        },
                        columns: {
                            columnVisibilityModel: columnVisibilityModel,
                        }
                    }}
                    // Forcer la colonne 'action' à rester visible
                    columnVisibilityModel={columnVisibilityModel}
                    onColumnVisibilityModelChange={(newModel) => {
                        setColumnVisibilityModel({
                            ...newModel,
                            [LAST_COLUMN_FIELD]: true, // re-force si l’utilisateur tente de cacher
                        });
                    }}

                    // Sélection
                    rowSelection
                    rowSelectionModel={selectionModel}
                    onRowSelectionModelChange={setSelectionModel}

                    showCellVerticalBorder
                    showColumnVerticalBorder={false}
                    checkboxSelection

                    sx={{
                        '& .MuiDataGrid-columnHeaders': { borderBottom: '1px solid #e0e0e0' },
                        '& .MuiDataGrid-columnHeader:last-child': { borderRight: 'none' },
                        '& .MuiDataGrid-columnHeaders:last-child': { borderRight: 'none' },
                        '& .MuiDataGrid-cell': { borderBottom: '1px solid #e0e0e0' },
                        '& .MuiDataGrid-cell:last-child': { borderRight: 'none' },
                        '& .MuiDataGrid-columnSeparator': { visibility: 'hidden' },
                        // Rendre le menu "3 points" TOUJOURS visible pour la colonne action
                        '& .MuiDataGrid-columnHeader.always-menu .MuiDataGrid-menuIcon': {
                            display: 'flex',
                            opacity: 1,
                            visibility: 'visible',
                            width: 'auto',
                        },
                        '& .MuiDataGrid-columnHeader.always-menu .MuiDataGrid-menuIconButton': {
                            opacity: 1,
                            visibility: 'visible',
                        },
                    }}
                />
            </Paper>
        </Box>
    );
};

export default DecomptesTable;
